import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { deskApi } from '@/api/desk.api';
import { showApiError } from '@/utils/apiError';
import type { DeskStatus, DeskWorkflow, DeskWorkflowTransition } from '@/types/desk';
import { notify } from '@/core/toast/notify';

export function DeskWorkflowDesignerPage() {
  const [workflows, setWorkflows] = useState<DeskWorkflow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [transitions, setTransitions] = useState<DeskWorkflowTransition[]>([]);
  const [statuses, setStatuses] = useState<DeskStatus[]>([]);
  const [workflowName, setWorkflowName] = useState('');
  const [fromStatusId, setFromStatusId] = useState<number | ''>('');
  const [toStatusId, setToStatusId] = useState<number | ''>('');
  const [roleCode, setRoleCode] = useState('');
  const [saving, setSaving] = useState(false);
  const { rows: paginatedTransitions, tablePaginationProps: transitionsPaginationProps } =
    useClientPaginatedRows(transitions);

  const loadWorkflows = useCallback(() => {
    Promise.all([deskApi.listWorkflows(), deskApi.listStatuses()])
      .then(([w, s]) => {
        setWorkflows(w);
        setStatuses(s);
        if (w.length > 0 && selectedId == null) {
          setSelectedId(w[0].id);
        }
      })
      .catch((err) => showApiError(err, 'Failed to load workflows'));
  }, [selectedId]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    if (selectedId == null) {
      setTransitions([]);
      return;
    }
    deskApi
      .listWorkflowTransitions(selectedId)
      .then(setTransitions)
      .catch((err) => showApiError(err, 'Failed to load transitions'));
  }, [selectedId]);

  const createWorkflow = async (e: FormEvent) => {
    e.preventDefault();
    if (!workflowName.trim()) return;
    setSaving(true);
    try {
      const created = await deskApi.createWorkflow({ name: workflowName.trim() });
      setWorkflowName('');
      notify.success('Workflow created');
      setSelectedId(created.id);
      loadWorkflows();
    } catch (err) {
      showApiError(err, 'Failed to create workflow');
    } finally {
      setSaving(false);
    }
  };

  const addTransition = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedId == null) return;
    setSaving(true);
    try {
      await deskApi.createWorkflowTransition(selectedId, {
        fromStatusId: fromStatusId === '' ? undefined : fromStatusId,
        toStatusId: toStatusId === '' ? undefined : toStatusId,
        allowedRoleCode: roleCode.trim() || undefined,
      });
      setFromStatusId('');
      setToStatusId('');
      setRoleCode('');
      notify.success('Transition added');
      setTransitions(await deskApi.listWorkflowTransitions(selectedId));
    } catch (err) {
      showApiError(err, 'Failed to add transition');
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = (id?: number) => statuses.find((s) => s.id === id)?.name ?? (id != null ? `#${id}` : 'Any');

  return (
    <div>
      <PageHeader
        title="Workflow designer"
        description="Define allowed status transitions and role ACLs"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Desk', to: '/desk' },
          { label: 'Setup', to: '/desk/setup' },
          { label: 'Workflows' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4 space-y-4">
          <h3 className="font-semibold">Workflows</h3>
          <ul className="space-y-2">
            {workflows.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  className={`w-full text-left rounded-lg border p-3 text-sm ${
                    selectedId === w.id ? 'border-primary-500 bg-primary-50' : 'border-slate-200'
                  }`}
                  onClick={() => setSelectedId(w.id)}
                >
                  <div className="font-medium">{w.name}</div>
                  <div className="text-xs text-slate-500">{w.active ? 'Active' : 'Inactive'}</div>
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={createWorkflow} className="space-y-2 border-t pt-4">
            <Input label="New workflow name" value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} />
            <Button type="submit" loading={saving}>Create workflow</Button>
          </form>
        </div>

        <div className="card p-4 lg:col-span-2 space-y-4">
          <h3 className="font-semibold">Transitions</h3>
          {selectedId == null ? (
            <p className="text-sm text-slate-500">Select or create a workflow</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2">From</th>
                      <th className="text-left p-2">To</th>
                      <th className="text-left p-2">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transitions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-4 text-slate-500">No transitions yet</td>
                      </tr>
                    ) : (
                      paginatedTransitions.map((t) => (
                        <tr key={t.id} className="border-t">
                          <td className="p-2">{statusLabel(t.fromStatusId)}</td>
                          <td className="p-2">{statusLabel(t.toStatusId)}</td>
                          <td className="p-2">{t.allowedRoleCode || 'Any'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <TablePagination {...transitionsPaginationProps} hasRows={transitions.length > 0} />
              <form onSubmit={addTransition} className="grid gap-3 sm:grid-cols-2 border-t pt-4">
                <Select
                  label="From status"
                  placeholder="Any"
                  options={statuses.map((s) => ({ value: String(s.id), label: s.name }))}
                  value={fromStatusId === '' ? '' : String(fromStatusId)}
                  onChange={(v) => setFromStatusId(v ? Number(v) : '')}
                />
                <Select
                  label="To status"
                  placeholder="Any"
                  options={statuses.map((s) => ({ value: String(s.id), label: s.name }))}
                  value={toStatusId === '' ? '' : String(toStatusId)}
                  onChange={(v) => setToStatusId(v ? Number(v) : '')}
                />
                <Input label="Allowed role code (optional)" value={roleCode} onChange={(e) => setRoleCode(e.target.value)} />
                <div className="flex items-end">
                  <Button type="submit" loading={saving}>Add transition</Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
